import React from "react";
import { hobbies } from "../mock";
import { useRole } from "../context/RoleContext";
import {
  Plane,
  Music,
  Sparkles,
  UtensilsCrossed,
  CircleDot,
  Volleyball,
  Cookie,
  Dumbbell,
  Film
} from "lucide-react";

const ICONS = {
  Plane,
  Music,
  Sparkles,
  UtensilsCrossed,
  CircleDot,
  Volleyball,
  Cookie,
  Dumbbell,
  Film
};

const CARD_PALETTE = {
  travel: { bg: "#FF8A5B", fg: "#121826", muted: "#121826AA" },
  piano: { bg: "#2A2F45", fg: "#FFF3E9", muted: "#FFF3E9B3" },
  "ai-tools": { bg: "#FFD2A6", fg: "#121826", muted: "#121826AA" },
  food: { bg: "#FF8A5B", fg: "#121826", muted: "#121826AA" },
  badminton: { bg: "#FFD2A6", fg: "#121826", muted: "#121826AA" },
  pickleball: { bg: "#2A2F45", fg: "#FFF3E9", muted: "#FFF3E9B3" },
  snacks: { bg: "#FF8A5B", fg: "#121826", muted: "#121826AA" },
  "running-gym": { bg: "#FFD2A6", fg: "#121826", muted: "#121826AA" },
  netflix: { bg: "#2A2F45", fg: "#FFF3E9", muted: "#FFF3E9B3" }
};

const TILTS = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "-rotate-1", "rotate-1", "-rotate-2", "rotate-1", "-rotate-1"];

const HobbyCard = ({ card, idx }) => {
  const Icon = ICONS[card.icon] || Sparkles;
  const palette = CARD_PALETTE[card.id] || CARD_PALETTE.travel;
  const tilt = TILTS[idx % TILTS.length];

  return (
    <div
      data-testid={`hobby-${card.id}`}
      className={`group relative overflow-hidden rounded-2xl px-4 py-4 cursor-default ${tilt}
        shadow-[0_10px_24px_-12px_rgba(0,0,0,0.35)]
        hover:shadow-[0_18px_36px_-12px_rgba(0,0,0,0.45)]
        hover:rotate-0 hover:-translate-y-1.5 hover:scale-[1.05]
        transition-all duration-300 ease-out`}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    >
      {/* tape strip */}
      <span
        aria-hidden
        className="absolute -top-2 left-3 w-10 h-3 opacity-50"
        style={{ background: "rgba(255,255,255,0.6)", transform: "rotate(-8deg)" }}
      />

      {/* icon */}
      <div
        className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-sm group-hover:-rotate-12 group-hover:scale-110 transition-all duration-300 mb-3"
      >
        <Icon className="w-4 h-4" strokeWidth={2} style={{ color: palette.bg }} />
      </div>

      <div
        className="display text-[15px] tracking-[-0.005em] leading-tight"
        style={{ color: palette.fg }}
      >
        {card.title}
      </div>

      {card.subtitle && (
        <div
          className="mt-1 text-[11.5px] leading-snug"
          style={{ color: palette.muted }}
        >
          {card.subtitle}
        </div>
      )}
    </div>
  );
};

const Hobbies = () => {
  const { hobbiesItalic } = useRole();
  return (
    <section
      id="off-screen"
      data-testid="hobbies-section"
      className="relative overflow-hidden border-t-4 border-[#FF8A5B]"
      style={{ backgroundColor: "#FFF3E9" }}
    >
      {/* warm ambient blobs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-16 left-[10%] w-72 h-72 rounded-full blur-3xl opacity-70"
          style={{ backgroundColor: "#FF8A5B" }}
        />
        <div
          className="absolute top-1/3 right-[6%] w-72 h-72 rounded-full blur-3xl opacity-60"
          style={{ backgroundColor: "#FFD2A6" }}
        />
        <div
          className="absolute -bottom-16 left-[35%] w-80 h-80 rounded-full blur-3xl opacity-40"
          style={{ backgroundColor: "#FF8A5B" }}
        />
        <div
          className="absolute top-1/4 left-[55%] w-60 h-60 rounded-full blur-3xl opacity-50"
          style={{ backgroundColor: "#FFD2A6" }}
        />
      </div>

      <div className="relative container-x py-14 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <span
              className="mono text-[11px] tracking-[0.18em] uppercase mb-2 inline-block"
              style={{ color: "#FF8A5B" }}
            >
              {hobbies.eyebrow}
            </span>
            <h2 className="display text-[34px] md:text-[48px] tracking-[-0.02em] leading-[1.02] font-bold text-[#121826]">
              {hobbies.heading}{" "}
              <span
                className="serif-italic font-normal"
                style={{ color: "#FF8A5B" }}
              >
                {hobbiesItalic}
              </span>
            </h2>
          </div>
          <p className="text-[13px] text-[#2A2F45] md:text-right max-w-sm">
            {hobbies.subhead}
          </p>
        </div>

        {/* sticker board grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {hobbies.cards.map((c, i) => (
            <HobbyCard key={c.id} card={c} idx={i} />
          ))}
        </div>

        <p
          className="mt-10 text-center mono text-[11px] tracking-[0.22em]"
          style={{ color: "#2A2F45" }}
        >
          <span style={{ color: "#FF8A5B" }}>✦</span> ALSO HAPPY TO TALK ABOUT ANY OF THE ABOVE OVER COFFEE IN LONDON{" "}
          <span style={{ color: "#FF8A5B" }}>✦</span>
        </p>
      </div>
    </section>
  );
};

export default Hobbies;
